import React, { useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import './css/SettingsPage.css';
import '../styles/burger-menu.css';
import { useAuth } from '../contexts/AuthContext';
import { API_ORIGIN } from '../api';
import useScrollDirection from '../hooks/useScrollDirection';

function SettingsPage() {
  const { authToken, logout } = useAuth();
  const { scrollDirection, isAtTop } = useScrollDirection();
  const [userDetails, setUserDetails] = useState({
    firstName: '',
    lastName: '',
    email: '',
    isEmailVerified: false,
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isLoading, setIsLoading] = useState(false);

  // Decode JWT to extract user ID
  const getUserIdFromToken = useCallback(() => {
    try {
      const decoded = jwtDecode(authToken);
      return decoded.sub; // Or the appropriate claim that holds the userId
    } catch (error) {
      return null;
    }
  }, [authToken]);

  // Fetch user details
  const fetchUserDetails = useCallback(async () => {
    const userId = getUserIdFromToken();
    if (!userId) {
      logout();
      return;
    }

    const response = await fetch(`${API_ORIGIN}/v1/users/${userId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      }
    });

    if (response.ok) {
      const data = await response.json();
      setUserDetails({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        isEmailVerified: data.isEmailVerified || false,
      });
    } else {
      setMessage({ type: 'error', text: 'Failed to fetch user details' });
    }
  }, [authToken, logout, getUserIdFromToken]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  // Update user details
  const updateUserDetails = async (updatedDetails) => {
    const userId = getUserIdFromToken();
    if (!userId) {
      logout();
      return;
    }

    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_ORIGIN}/v1/users/${userId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(updatedDetails)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'User details updated successfully.' });
        fetchUserDetails(); // Refetch user details to reflect the update
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.message || 'Failed to update user details' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred while updating user details' });
    } finally {
      setIsLoading(false);
    }
  };

  // Change password
  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters long' });
      return;
    }

    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // First verify current password by attempting login
      const loginResponse = await fetch(`${API_ORIGIN}/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userDetails.email,
          password: passwordData.currentPassword,
        }),
      });

      if (!loginResponse.ok) {
        setMessage({ type: 'error', text: 'Current password is incorrect' });
        setIsLoading(false);
        return;
      }

      // If current password is correct, update password
      const userId = getUserIdFromToken();
      const updateResponse = await fetch(`${API_ORIGIN}/v1/users/${userId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ password: passwordData.newPassword })
      });

      if (updateResponse.ok) {
        setMessage({ type: 'success', text: 'Password changed successfully.' });
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      } else {
        const errorData = await updateResponse.json();
        setMessage({ type: 'error', text: errorData.message || 'Failed to change password' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred while changing password' });
    } finally {
      setIsLoading(false);
    }
  };

  // Resend verification email
  const handleResendVerificationEmail = async () => {
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_ORIGIN}/v1/auth/send-verification-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
      });

      if (response.ok || response.status === 204) {
        setMessage({ type: 'success', text: 'Verification email sent. Please check your inbox.' });
      } else {
        setMessage({ type: 'error', text: 'Failed to send verification email' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred while sending verification email' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setUserDetails(prevDetails => ({ ...prevDetails, [name]: value }));
  };

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswordData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleSaveChanges = () => {
    updateUserDetails({
      firstName: userDetails.firstName,
      lastName: userDetails.lastName,
      email: userDetails.email,
    });
  };

  const handleMenuToggle = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    window.dispatchEvent(new CustomEvent('toggleNavigationMenu'));
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div className="page-header-with-menu">
          <button
            className={`page-header-menu-button ${scrollDirection === 'down' && !isAtTop ? 'hide-on-scroll' : ''}`}
            onClick={handleMenuToggle}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleMenuToggle(e);
            }}
            type="button"
            aria-label="Open menu"
          >
            <FontAwesomeIcon icon={faBars} />
          </button>
          <h1 className="settings-title page-title-mobile">Account Settings</h1>
          <h1 className="settings-title page-title-desktop">Account Settings</h1>
        </div>
      </div>

      <div className="settings-content">
        {message.text && (
          <div className={`settings-message ${message.type === 'success' ? 'settings-message-success' : 'settings-message-error'}`}>
            {message.text}
          </div>
        )}

        <div className="settings-card">
          <h2 className="settings-card-title">User Details</h2>
          <div className="settings-form">
            <div className="settings-form-group">
              <label className="settings-label">First Name</label>
              <input
                type="text"
                name="firstName"
                value={userDetails.firstName}
                onChange={handleInputChange}
                disabled={isLoading}
                className="settings-input"
              />
            </div>
            <div className="settings-form-group">
              <label className="settings-label">Last Name</label>
              <input
                type="text"
                name="lastName"
                value={userDetails.lastName}
                onChange={handleInputChange}
                disabled={isLoading}
                className="settings-input"
              />
            </div>
            <div className="settings-form-group">
              <label className="settings-label">Email</label>
              <input
                type="email"
                name="email"
                value={userDetails.email}
                onChange={handleInputChange}
                disabled={isLoading}
                className="settings-input"
              />
              <div className="email-verification-status">
                {userDetails.isEmailVerified ? (
                  <span className="verification-badge verified">✓ Email Verified</span>
                ) : (
                  <div className="verification-actions">
                    <span className="verification-badge not-verified">⚠ Email Not Verified</span>
                    <button
                      type="button"
                      onClick={handleResendVerificationEmail}
                      disabled={isLoading}
                      className="resend-verification-btn"
                    >
                      Resend Verification Email
                    </button>
                  </div>
                )}
              </div>
            </div>
            <button 
              onClick={handleSaveChanges} 
              disabled={isLoading}
              className="settings-button settings-button-primary"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        <div className="settings-card">
          <h2 className="settings-card-title">Change Password</h2>
          <div className="settings-form">
            <div className="settings-form-group">
              <label className="settings-label">Current Password</label>
              <input
                type="password"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                disabled={isLoading}
                className="settings-input"
              />
            </div>
            <div className="settings-form-group">
              <label className="settings-label">New Password</label>
              <input
                type="password"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                disabled={isLoading}
                className="settings-input"
              />
            </div>
            <div className="settings-form-group">
              <label className="settings-label">Confirm New Password</label>
              <input
                type="password"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                disabled={isLoading}
                className="settings-input"
              />
            </div>
            <button 
              onClick={handleChangePassword} 
              disabled={isLoading}
              className="settings-button settings-button-primary"
            >
              {isLoading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
